import {ICellRendererParams} from 'ag-grid-community';
import {Inject, LOCALE_ID} from '@angular/core';
import {
  CellWithLinkComponent,
  DatetimeRangeFloatingFilterComponent,
  DropdownFloatingFilterComponent,
  LinkParams,
  RestService,
  RestSimpleResponseParser,
  RestYiiFilterService,
  RestYiiPagination,
  RestYiiSort
} from 'gelios-ng-admin-components';
import {HttpClient} from '@angular/common/http';
import {BaseListComponent} from '../../base/base-list.component';
import {map} from 'rxjs/operators';
import {AuthorizationService} from '../../authorization/authorization.service';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ImmutableListRepositoryService} from '../../../lib/gelios-field/immutable-list-repository.service';
import {UnitLogModel} from './unit-log.model';
import {UnitLogService} from './unit-log.service';
import {NotificationsService} from 'angular2-notifications';
import {RouteLibraryService} from '../../route/route-library.service';
import {MainLightboxService} from '../../layout/main-lightbox.service';
import {ModalWindowService} from '../../shared/modal-window/modal-window.service';
import {timestampToFullDateTime} from '../../utility/timestamp-functions';
import {RouteLibraryEntities} from '../../route/route-library-entity';
import {ProjectBusinessLogicSyncFilterService} from '../../shared/project-business-logic-filter/project-business-logic-sync-filter.service';
import {GRID_FILTER_FIELDS} from '../../shared/grid/grid-filter-services/grid-filter.config';
import {GridFilterFieldEntities} from '../../shared/grid/grid-filter-services/grid-filter-field-entity';
import {GRID_FILTER_SERVICE_DEPS, GridFilterService} from '../../shared/grid/grid-filter-services/grid-filter.service';

export const BASE_UNIT_LOG_PROVIDERS = [
  {
    provide: GRID_FILTER_FIELDS,
    useValue: [
      GridFilterFieldEntities.PROJECT,
    ]
  },
  {
    provide: GridFilterService,
    useClass: GridFilterService,
    deps: GRID_FILTER_SERVICE_DEPS
  },
];

export abstract class UnitLogListBase extends BaseListComponent {
  timestampColumnWidth = 150;
  disableRowCount = true;
  timestampFloatingFilterComponentParams = {
    suppressFilterButton: true,
  };
  exportSpecialTypes = {
    timestamp: 'date',
    processed_at_timestamp: 'date'
  };

  getTableName(): string {
    return 'unit-log';
  }

  getModelName(): string {
    return 'unit-log';
  }

  createModel(): UnitLogModel {
    return new UnitLogModel(this.http);
  }

  createService(http: HttpClient, environmentVersion: string, environmentApiBasepoint: string): RestService<UnitLogModel> {
    return new UnitLogService<UnitLogModel>(http,
      new RestYiiFilterService(),
      new RestYiiSort(),
      new RestYiiPagination(),
      new RestSimpleResponseParser(),
      environmentVersion,
      environmentApiBasepoint
    );
  }

  get allColumns(): { [name: string]: any } {
    return {
      id: {
        headerName: 'ID',
        field: 'id',
        width: 100
      },
      project_custom_name: {
        headerName: 'Project',
        field: 'project_custom_name',
        width: 100,
        sort: 'false',
        suppressMenu: true,
        suppressSorting: true,
        floatingFilterComponentFramework: DropdownFloatingFilterComponent,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
          items: this.gridFilterService.projectList,
          value: this.gridFilterService.selectedProjectIdChanged.getValue(),
          valueChanged: this.gridFilterService.selectedProjectIdChanged,
        },
      },
      unit_name: {
        headerName: 'Unit',
        field: 'unit_name',
        width: 100,
        cellRendererFramework: CellWithLinkComponent,
        cellRendererParams: {
          linkParams: (params: ICellRendererParams): LinkParams => {
            const linkParams: LinkParams = {route: null, label: ''};

            // item exists:
            if (params.data && params.data.unit_id) {
              linkParams.route = this.routeLibrary.getRoute({
                entity: RouteLibraryEntities.UNIT,
                action: 'view',
                id: params.data.unit_id
              });
              linkParams.label = params.data.unit_name;
            }

            // item deleted, only name available:
            if (params.data && !params.data.unit_id && params.data.unit_name) {
              linkParams.route = null;
              linkParams.label = params.data.unit_name;
            }
            return linkParams;
          }
        }
      },
      timestamp: {
        headerName: 'Time',
        field: 'timestamp',
        valueFormatter: (p) => timestampToFullDateTime(p.value),
        width: this.timestampColumnWidth,
        floatingFilterComponentFramework: DatetimeRangeFloatingFilterComponent,
        floatingFilterComponentParams: this.timestampFloatingFilterComponentParams,
        filter: 'agNumberColumnFilter',
      },
      type: {
        headerName: 'Type',
        field: 'type',
        valueFormatter: (p) => p.value ? this.immutableList.getItemById('unit-log-type', p.value) : '',
        floatingFilterComponentFramework: DropdownFloatingFilterComponent,
        floatingFilterComponentParams: {
          value: null,
          suppressFilterButton: true,
          items: this.immutableList.getList('unit-log-type').pipe(
            map(items => items.map(item => ({label: item.name, value: item.id})))
          ),
        },
        width: 150
      },
      priority: {
        headerName: 'Priority',
        field: 'priority',
        valueFormatter: (p) => p.value ? this.immutableList.getItemById('unit-log-priority', p.value) : '',
        floatingFilterComponentFramework: DropdownFloatingFilterComponent,
        floatingFilterComponentParams: {
          value: null,
          suppressFilterButton: true,
          items: this.immutableList.getList('unit-log-priority').pipe(
            map(items => items.map(item => ({label: item.name, value: item.id})))
          ),
        },
        cellRenderer: (p) => {
          return p.valueFormatted;
        },
        cellClass: (p) => {
          if (p.value === 3) {
            return ['ag-cell-status', 'ag-cell-status-error'];
          }
          return null;
        },
      },
      source_type: {
        headerName: 'Source type',
        field: 'source_type',
        valueFormatter: (p) => p.value ? this.immutableList.getItemById('unit-log-source-type', p.value) : '',
        floatingFilterComponentFramework: DropdownFloatingFilterComponent,
        floatingFilterComponentParams: {
          value: null,
          suppressFilterButton: true,
          items: this.immutableList.getList('unit-log-source-type').pipe(
            map(items => items.map(item => ({label: item.name, value: item.id})))
          ),
        },
        width: 150
      },
      source_name: {
        headerName: 'Source name',
        field: 'source_name',
        width: 150,
        cellRendererFramework: CellWithLinkComponent,
        cellRendererParams: {
          linkParams: (params: ICellRendererParams): LinkParams => {
            const linkParams: LinkParams = {route: null, label: ''};

            // item exists:
            if (params.data && params.data.user_id) {
              linkParams.route = this.routeLibrary.getRoute({
                entity: RouteLibraryEntities.USER,
                action: 'view',
                id: params.data.user_id
              });
              linkParams.label = params.data.user_login;
            }

            // item deleted, only name available:
            if (params.data && !params.data.user_id && params.data.user_login) {
              linkParams.route = null;
              linkParams.label = params.data.user_login;
            }

            // item exists:
            if (params.data && params.data.end_user_id) {
              linkParams.route = this.routeLibrary.getRoute({
                entity: RouteLibraryEntities.END_USER,
                action: 'view',
                id: params.data.end_user_id
              });
              linkParams.label = params.data.end_user_login;
            }

            // item deleted, only name available:
            if (params.data && !params.data.end_user_id && params.data.end_user_login) {
              linkParams.route = null;
              linkParams.label = params.data.end_user_login;
            }

            // item exists:
            if (params.data && params.data.end_user_price_plan_id) {
              linkParams.route = this.routeLibrary.getRoute({
                entity: RouteLibraryEntities.END_USER_PRICE_PLAN,
                action: 'update',
                id: params.data.end_user_price_plan_id
              });
              linkParams.label = params.data.end_user_price_plan_name;
            }

            // item deleted, only name available:
            if (params.data && !params.data.end_user_price_plan_id && params.data.end_user_price_plan_name) {
              linkParams.route = null;
              linkParams.label = params.data.end_user_price_plan_name;
            }

            // item exists:
            if (params.data && params.data.notification_id) {
              linkParams.route = this.routeLibrary.getRoute({
                entity: RouteLibraryEntities.NOTIFICATION,
                action: 'view',
                id: params.data.notification_id
              });
              linkParams.label = params.data.notification_name;
            }

            // item deleted, only name available:
            if (params.data && !params.data.notification_id && params.data.notification_name) {
              linkParams.route = null;
              linkParams.label = params.data.notification_name;
            }

            if (params.data && params.data.end_user_order_id) {
              linkParams.route = this.routeLibrary.getRoute({
                entity: RouteLibraryEntities.END_USER_ORDER,
                action: 'view',
                id: params.data.end_user_order_id
              });
              linkParams.label = params.data.end_user_order_id;
            }
            return linkParams;
          },
        }
      },
      description: {
        headerName: 'Description',
        field: 'description',
        width: 150
      },
      command_description: {
        headerName: 'Command',
        field: 'command_description',
        width: 150
      },
      processed_by_user_login: {
        headerName: 'Closed by',
        field: 'processed_by_user_login',
        width: 150,
        cellRendererFramework: CellWithLinkComponent,
        cellRendererParams: {
          linkParams: (params: ICellRendererParams): LinkParams => {
            const linkParams: LinkParams = {route: null, label: ''};

            // item exists:
            if (params.data && params.data.processed_by_user_id) {
              linkParams.route = this.routeLibrary.getRoute({
                entity: RouteLibraryEntities.USER,
                action: 'view',
                id: params.data.processed_by_user_id
              });
              linkParams.label = params.data.processed_by_user_login;
            }

            // item deleted, only name available:
            if (params.data && !params.data.processed_by_user_id && params.data.processed_by_user_login) {
              linkParams.route = null;
              linkParams.label = params.data.processed_by_user_login;
            }
            return linkParams;
          }
        }
      },
      processed_comment: {
        headerName: 'Reason',
        field: 'processed_comment',
        width: 150
      },
      processed_at_timestamp: {
        headerName: 'Time of closing',
        field: 'processed_at_timestamp',
        width: 150,
        valueFormatter: (p) => timestampToFullDateTime(p.value),
        floatingFilterComponentFramework: DatetimeRangeFloatingFilterComponent,
        filter: 'agNumberColumnFilter',
      },
    };
  }

  constructor(
    protected authorizationService: AuthorizationService,
    public route: Router,
    public activatedRoute: ActivatedRoute,
    protected translate: TranslateService,
    protected notif: NotificationsService,
    protected http: HttpClient,
    public projectBusinessLogicSyncFilterService: ProjectBusinessLogicSyncFilterService,
    protected routeLibrary: RouteLibraryService,
    public mainLightboxService: MainLightboxService,
    @Inject('ENVIRONMENT_VERSION') environmentVersion: string,
    @Inject('ENVIRONMENT_API_BASEPOINT') environmentApiBasepoint: string,
    @Inject(LOCALE_ID) localeId: string,
    protected immutableList: ImmutableListRepositoryService,
    protected modalWindowService: ModalWindowService,
    protected gridFilterService: GridFilterService,
  ) {
    super(
      authorizationService,
      route,
      activatedRoute,
      translate,
      notif,
      http,
      projectBusinessLogicSyncFilterService,
      routeLibrary,
      mainLightboxService,
      environmentVersion,
      environmentApiBasepoint,
      localeId,
      modalWindowService
    );
  }

}
